import { ButtonField, Smokebreak } from '@wk/components-react16';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import OngoingEvaluationItem from '../OngoingEvaluation';
import useOnGoingEvaluationsStore from '@/features/ongoingEvaluations/stores/ongoingEvaluationsStore';
import type { EvaluationStatusResponse, OngoingEvaluation } from '@/types';
import { getEvaluationJobStatus } from '@/domains/evaluation/evaluationApi';
import {
  fetchSiteComplianceJobStatus,
  isSiteComplianceJobTerminalStatus,
  trackSiteComplianceEvent,
  type JobStatusResponse,
} from '@/domains/siteCompliance';
import { retry } from '@/lib/retry';
import { useJobPolling } from '@/portals/siteCompliance/hooks/useJobPolling';

interface OngoingEvaluationsProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EvaluationStatusError {
  response?: {
    data?: {
      message?: string;
      title?: string;
    };
  };
}

type OngoingEvaluationStatusResponse =
  | EvaluationStatusResponse
  | JobStatusResponse;

const activeEvaluationStatuses: Array<OngoingEvaluation['status']> = [
  'Running',
  'Pending',
];

const terminalEvaluationStatuses: Array<OngoingEvaluation['status']> = [
  'Completed',
  'Failed',
  'TimedOut',
];

const isSiteComplianceEvaluation = (evaluation: OngoingEvaluation) =>
  evaluation.source === 'site-compliance';

const OngoingEvaluations: FC<OngoingEvaluationsProps> = ({
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const {
    ongoingEvaluations,
    removeOngoingEvaluation,
    clearAllOngoingEvaluations,
    updateEvaluations,
  } = useOnGoingEvaluationsStore(
    useShallow((state) => ({
      ongoingEvaluations: state.ongoingEvaluations,
      removeOngoingEvaluation: state.removeOngoingEvaluation,
      clearAllOngoingEvaluations: state.clearAllOngoingEvaluations,
      updateEvaluations: state.updateEvaluations,
    })),
  );

  const handleDelete = useCallback((jobId: string) => {
    removeOngoingEvaluation(jobId);
  }, [removeOngoingEvaluation]);

  const handleClearAll = useCallback(() => {
    clearAllOngoingEvaluations();
  }, [clearAllOngoingEvaluations]);

  const sortedOngoingEvaluations = useMemo(() => {
    const getStatusPriority = (status: OngoingEvaluation['status']) =>
      activeEvaluationStatuses.includes(status) ? 0 : 1;

    return [...ongoingEvaluations].sort((left, right) => {
      const priorityDifference =
        getStatusPriority(left.status) - getStatusPriority(right.status);

      if (priorityDifference !== 0) {
        return priorityDifference;
      }

      return (
        new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime()
      );
    });
  }, [ongoingEvaluations]);

  const getErrorMessage = useCallback((error: unknown) => {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
      return undefined;
    }

    const responseData = (error as EvaluationStatusError).response?.data;
    return responseData?.title ?? responseData?.message;
  }, []);

  const removeOldFailedEvaluations = useCallback(() => {
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const oldFailedEvaluations = ongoingEvaluations.filter(
      (evaluation) =>
        (evaluation.status === 'Failed' || evaluation.status === 'TimedOut') &&
        new Date(evaluation.startedAt) < twentyFourHoursAgo,
    );
    oldFailedEvaluations.forEach((evaluation) => {
      removeOngoingEvaluation(evaluation.jobId);
    });
  }, [ongoingEvaluations, removeOngoingEvaluation]);

  const pollEvaluationStatus = useCallback(
    async (
      evaluation: OngoingEvaluation,
    ): Promise<OngoingEvaluationStatusResponse> => {
      if (isSiteComplianceEvaluation(evaluation)) {
        return fetchSiteComplianceJobStatus(evaluation.jobId);
      }

      const res = await retry(() => getEvaluationJobStatus(evaluation.jobId));
      return res.data;
    },
    [],
  );

  const normalizeStatusResponse = useCallback(
    (
      evaluation: OngoingEvaluation,
      data: OngoingEvaluationStatusResponse,
    ): Partial<OngoingEvaluation> => {
      const status = (data.status ?? 'Pending') as OngoingEvaluation['status'];

      if (!isSiteComplianceEvaluation(evaluation)) {
        return {
          output: (data as EvaluationStatusResponse).output,
          status,
        };
      }

      const siteComplianceData = data as JobStatusResponse;
      const evaluationId =
        siteComplianceData.evaluationId ??
        siteComplianceData.output?.recordId ??
        siteComplianceData.output?.evaluationId ??
        null;
      const detailedMessage =
        (siteComplianceData.output?.errorMessage as string | undefined) ??
        (siteComplianceData.output?.error as string | undefined) ??
        siteComplianceData.output?.message ??
        siteComplianceData.message ??
        '';

      const output = evaluationId
        ? {
            bitId:
              siteComplianceData.siteId ??
              evaluation.siteId ??
              evaluation.bitId,
            duration: siteComplianceData.output?.duration ?? '',
            message: detailedMessage,
            recordId: evaluationId,
            runId:
              siteComplianceData.output?.runId ??
              siteComplianceData.jobId ??
              evaluation.jobId,
            status,
          }
        : null;

      return {
        evaluationId,
        message: detailedMessage || undefined,
        output,
        siteId: siteComplianceData.siteId ?? evaluation.siteId,
        status,
      };
    },
    [],
  );

  const trackSiteComplianceCompletion = useCallback(
    (
      evaluation: OngoingEvaluation,
      updates: Partial<OngoingEvaluation>,
    ) => {
      if (!isSiteComplianceEvaluation(evaluation) || !updates.status) {
        return;
      }

      if (!terminalEvaluationStatuses.includes(updates.status)) {
        return;
      }

      const startedAt = new Date(evaluation.startedAt).getTime();
      const latencyMs = Number.isFinite(startedAt)
        ? Date.now() - startedAt
        : undefined;

      trackSiteComplianceEvent('SiteComplianceJobCompleted', {
        evaluationId: updates.evaluationId ?? evaluation.evaluationId ?? '',
        jobId: evaluation.jobId,
        latencyMs,
        siteId: updates.siteId ?? evaluation.siteId ?? evaluation.bitId,
        status: updates.status,
      });
      trackSiteComplianceEvent('SiteComplianceJobPollingDuration', {
        evaluationId: updates.evaluationId ?? evaluation.evaluationId ?? '',
        jobId: evaluation.jobId,
        latencyMs,
        siteId: updates.siteId ?? evaluation.siteId ?? evaluation.bitId,
        status: updates.status,
      });
    },
    [],
  );

  const handleJobStatus = useCallback(
    (
      evaluation: OngoingEvaluation,
      data: OngoingEvaluationStatusResponse,
    ) => {
      const updates = normalizeStatusResponse(evaluation, data);
      const targetJobId = data.jobId ?? evaluation.jobId;

      updateEvaluations({
        [targetJobId]: updates,
      });

      if (
        updates.status &&
        terminalEvaluationStatuses.includes(updates.status)
      ) {
        if (isSiteComplianceEvaluation(evaluation)) {
          queryClient.invalidateQueries({
            queryKey: ['site-compliance', 'evaluations'],
          });
          queryClient.invalidateQueries({
            queryKey: ['site-compliance', 'evaluation'],
          });
          trackSiteComplianceCompletion(evaluation, updates);
        } else {
          queryClient.invalidateQueries({ queryKey: ['evaluations'] });
        }
      }
    },
    [
      normalizeStatusResponse,
      queryClient,
      trackSiteComplianceCompletion,
      updateEvaluations,
    ],
  );

  const handleJobError = useCallback(
    (evaluation: OngoingEvaluation, error: unknown) => {
      if (isSiteComplianceEvaluation(evaluation)) {
        console.error('Site Compliance job status polling failed', {
          error,
          evaluationId: evaluation.evaluationId ?? null,
          jobId: evaluation.jobId,
          siteId: evaluation.siteId ?? evaluation.bitId,
        });
      }

      updateEvaluations({
        [evaluation.jobId]: {
          status: 'Failed',
          message: getErrorMessage(error) ?? 'Unable to load job status.',
        },
      });
    },
    [getErrorMessage, updateEvaluations],
  );

  const getPollingJobId = useCallback(
    (evaluation: OngoingEvaluation) => evaluation.jobId,
    [],
  );
  const isPollingJobActive = useCallback(
    (evaluation: OngoingEvaluation) =>
      activeEvaluationStatuses.includes(evaluation.status),
    [],
  );
  const isPollingResultTerminal = useCallback(
    (result: OngoingEvaluationStatusResponse) =>
      isSiteComplianceJobTerminalStatus(
        result.status as JobStatusResponse['status'],
      ),
    [],
  );
  const handlePollDuration = useCallback(
    (
      evaluation: OngoingEvaluation,
      durationMs: number,
      result: OngoingEvaluationStatusResponse | null,
      error: unknown,
    ) => {
      if (!isSiteComplianceEvaluation(evaluation)) {
        return;
      }

      const siteComplianceResult = result as JobStatusResponse | null;
      const statusCode =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: { status?: unknown } }).response?.status ===
          'number'
          ? (error as { response: { status: number } }).response.status
          : undefined;

      trackSiteComplianceEvent('SiteComplianceJobPollingDuration', {
        durationMs,
        evaluationId:
          siteComplianceResult?.evaluationId ?? evaluation.evaluationId ?? null,
        jobId: evaluation.jobId,
        siteId:
          siteComplianceResult?.siteId ?? evaluation.siteId ?? evaluation.bitId,
        statusCode,
      });
    },
    [],
  );

  useJobPolling<OngoingEvaluation, OngoingEvaluationStatusResponse>({
    getJobId: getPollingJobId,
    isActiveJob: isPollingJobActive,
    isTerminalResult: isPollingResultTerminal,
    jobs: ongoingEvaluations,
    onAfterPoll: removeOldFailedEvaluations,
    onJobError: handleJobError,
    onJobStatus: handleJobStatus,
    onPollDuration: handlePollDuration,
    pollJob: pollEvaluationStatus,
  });

  return (
    <Smokebreak
      isOpen={isOpen}
      position="right"
      onUserRequest={() => onClose()}
    >
      <h2 slot="smokebreakHeader" className="p-3 font-semibold text-lg">
        Ongoing Evaluations
      </h2>
      <div className="p-3" slot="smokebreakBody">
        {ongoingEvaluations.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No ongoing evaluations
          </div>
        ) : (
          <div className="space-y-2">
            {sortedOngoingEvaluations.map((evaluation) => (
              <OngoingEvaluationItem
                key={evaluation.jobId}
                evaluation={evaluation}
                onDelete={handleDelete}
                onViewResults={() => {
                  onClose();
                }}
              />
            ))}
            {ongoingEvaluations.length > 1 &&
              <div className="flex justify-end">
                <ButtonField mode="text" isDanger>
                  <button onClick={handleClearAll}>Clear All</button>
                </ButtonField>
              </div>}
          </div>
        )}
      </div>
    </Smokebreak>
  );
};

export default OngoingEvaluations;
